import { errorCodeOf } from "@/shared/api/error-code";
import { ApiError } from "@/shared/api/http-client";

type Translate = (key: string, values?: Record<string, unknown>) => string;

const BASE = "organizationAccess.errors";

/** API `errorCode` -> copy. Unknown codes fall through to the status, then generic. */
const ERROR_KEY_BY_CODE: Readonly<Record<string, string>> = {
  ORGANIZATION_NOT_FOUND: `${BASE}.organizationNotFound`,
  MEMBERSHIP_NOT_FOUND: `${BASE}.membershipNotFound`,
  STAFF_USER_NOT_FOUND: `${BASE}.userNotFound`,
  USER_NOT_FOUND: `${BASE}.userNotFound`,
  STAFF_USER_NOT_ASSIGNABLE: `${BASE}.userNotEligible`,
  ALREADY_MEMBER: `${BASE}.alreadyMember`,
  OWNER_MEMBERSHIP_PROTECTED: `${BASE}.ownerImmutable`,
  MEMBER_NOT_ACTIVE: `${BASE}.memberNotActive`,
  SELF_ROLE_CHANGE_FORBIDDEN: `${BASE}.selfRoleChange`,
  MEMBERSHIP_STATE_CHANGED: `${BASE}.stateChanged`,
  MEMBERSHIP_ROLE_UNCHANGED: `${BASE}.roleUnchanged`,
  MEMBER_NOT_STAFF: `${BASE}.memberNotStaff`,
  LOCATIONS_NOT_ALLOWED_FOR_ADMIN: `${BASE}.locationsNotAllowedForAdmin`,
  TEST_PARTITION_MISMATCH: `${BASE}.testPartitionMismatch`,
  ENTITY_NOT_OWNED: `${BASE}.locationGone`,
  VENUE_NOT_FOUND: `${BASE}.locationGone`,
  PROPERTY_NOT_FOUND: `${BASE}.locationGone`,
  ORGANIZATION_KIND_MISMATCH: `${BASE}.locationNotInOrganization`,
  ENTITY_KIND_MISMATCH: `${BASE}.locationNotInOrganization`,
  NO_ENTITIES: `${BASE}.noLocationsSelected`,
  LOCATION_IN_ADD_AND_REMOVE: `${BASE}.locationInAddAndRemove`,
  MEMBER_ACCOUNT_INACTIVE: `${BASE}.memberAccountInactive`,
  INVALID_ORGANIZATION_NAME: `${BASE}.invalidName`,
};

/** The access-model copy key for an API `errorCode`, when this module owns one. */
export function accessErrorKeyForCode(code: string): string | undefined {
  return ERROR_KEY_BY_CODE[code];
}

/**
 * Codes that mean "the membership you were looking at is not what the server
 * has any more" (somebody else changed it, or the page was stale). The reads
 * behind the screen must be refreshed so the next attempt starts from the truth.
 */
const STALE_STATE_CODES: ReadonlySet<string> = new Set([
  "MEMBERSHIP_STATE_CHANGED",
  "MEMBERSHIP_NOT_FOUND",
  "ALREADY_MEMBER",
  "MEMBER_NOT_ACTIVE",
  "MEMBERSHIP_ROLE_UNCHANGED",
  "MEMBER_NOT_STAFF",
  "ORGANIZATION_NOT_FOUND",
]);

export function isStaleStateError(err: unknown): boolean {
  const code = errorCodeOf(err);
  return code !== null && STALE_STATE_CODES.has(code);
}

/**
 * The one stale-state code where the membership is still there and a plain
 * retry (after the refresh) can succeed, so a dialog keeps what the operator
 * picked instead of closing: somebody changed the membership at the same time.
 */
export function isRetryableStateError(err: unknown): boolean {
  return errorCodeOf(err) === "MEMBERSHIP_STATE_CHANGED";
}

/** A location the operator picked was deleted or moved away; nothing was written. */
const LOCATION_GONE_CODES: ReadonlySet<string> = new Set([
  "VENUE_NOT_FOUND",
  "PROPERTY_NOT_FOUND",
  "ENTITY_NOT_OWNED",
]);

export function isLocationGoneError(err: unknown): boolean {
  const code = errorCodeOf(err);
  return code !== null && LOCATION_GONE_CODES.has(code);
}

export type ExistingMemberRole = "OWNER" | "ADMIN" | "STAFF";

const EXISTING_MEMBER_ROLES: ReadonlySet<string> = new Set(["OWNER", "ADMIN", "STAFF"]);

/**
 * `ALREADY_MEMBER` carries the existing row in `details.membershipRole`: the
 * conflict (role, or null when the API sent nothing usable), or null for any
 * other error. The dialogs use it to name the role instead of a bare refusal.
 */
export function alreadyMemberConflict(
  err: unknown,
): { readonly role: ExistingMemberRole | null } | null {
  if (errorCodeOf(err) !== "ALREADY_MEMBER" || !(err instanceof ApiError)) return null;
  const details = (err.body as { details?: unknown }).details;
  const role =
    details && typeof details === "object"
      ? (details as { membershipRole?: unknown }).membershipRole
      : undefined;
  return {
    role:
      typeof role === "string" && EXISTING_MEMBER_ROLES.has(role)
        ? (role as ExistingMemberRole)
        : null,
  };
}

/**
 * What went wrong with an access write (add member, change role, assign locations, reactivate, rename), as
 * our own copy: the server message is never shown (it can name people).
 * Side-effect free.
 */
export function resolveAccessError(err: unknown, t: Translate): string {
  const code = errorCodeOf(err);
  const byCode = code ? ERROR_KEY_BY_CODE[code] : undefined;
  if (byCode) return t(byCode);
  if (err instanceof ApiError && err.status === 403) return t(`${BASE}.forbidden`);
  if (err instanceof ApiError && err.status === 429) return t(`${BASE}.throttled`);
  // A 400 without one of our codes is the DTO validation (e.g. too many locations).
  if (err instanceof ApiError && err.status === 400) return t(`${BASE}.invalidRequest`);
  return t(`${BASE}.generic`);
}
